// Datenquellen (Newsticker/Feed, Termine, Tabelle): normalisiertes Datenformat, wie es der Server aus RSS/Atom,
// iCal/ICS und CSV/XLSX erzeugt und im Manifest (payload.dataSources) an die Player verteilt. Die Player holen nie
// selbst etwas aus dem Internet; Bilder von Feeds sind Assets (sha256) des Manifests.
// Gemeinsam für Server (Normalisierung, Feldbelegung), Engine/Vorschau (Bausteine) und Manager.
import { z } from 'zod';
import { tzOffsetMs } from './analog.js';
const sha256 = z.string().regex(/^[0-9a-f]{64}$/);
/** Kalendertag "YYYY-MM-DD" oder Zeitpunkt (ISO 8601 mit Zone). */
const dateOrInstant = z.string().max(40);
export const DATA_KINDS = ['feed', 'calendar', 'table'];
export const FeedItemSchema = z.object({
    title: z.string(),
    text: z.string().optional(),
    /** Veröffentlichung (ISO 8601). */
    date: z.string().optional(),
    /** Bild als Asset (sha256), vom Server geladen und neu kodiert. */
    image: sha256.optional(),
    author: z.string().optional(),
});
export const FeedDataSchema = z.object({
    kind: z.literal('feed'),
    title: z.string().optional(),
    items: z.array(FeedItemSchema),
});
export const CalendarEventSchema = z.object({
    id: z.string(),
    title: z.string(),
    /** Ganztägig: Kalendertag "YYYY-MM-DD"; sonst Zeitpunkt in UTC. */
    start: dateOrInstant,
    /** Ganztägig: erster Tag NACH dem Termin (exklusiv); sonst Zeitpunkt in UTC. */
    end: dateOrInstant,
    allDay: z.boolean(),
    location: z.string().optional(),
    description: z.string().optional(),
});
export const CalendarDataSchema = z.object({
    kind: z.literal('calendar'),
    name: z.string().optional(),
    /** Zeitzone des Kalenders (Information; Anzeige in der Zeitzone des Bildschirms). */
    timeZone: z.string().optional(),
    /** Zeitraum, für den Wiederholungen aufgelöst wurden. */
    from: z.string(),
    until: z.string(),
    events: z.array(CalendarEventSchema),
});
export const TableColumnSchema = z.object({
    key: z.string(),
    label: z.string(),
    type: z.enum(['text', 'number', 'date', 'time', 'datetime']).optional(),
});
export const TableDataSchema = z.object({
    kind: z.literal('table'),
    columns: z.array(TableColumnSchema),
    /** Zellen als Text, gleiche Reihenfolge wie columns. Datum "YYYY-MM-DD", Uhrzeit "HH:MM". */
    rows: z.array(z.array(z.string())),
    /** Zeilen wurden wegen der Grössengrenze abgeschnitten. */
    truncated: z.boolean().optional(),
});
export const DataSetSchema = z.discriminatedUnion('kind', [FeedDataSchema, CalendarDataSchema, TableDataSchema]);
/** Liest normalisierte Daten; unbekannte oder ungültige Daten → null (Baustein zeigt dann einen Hinweis). */
export function parseDataSet(raw) {
    const r = DataSetSchema.safeParse(raw);
    return r.success ? r.data : null;
}
/** Bausteine, die eine Datenquelle anzeigen, und die Art der Daten, die sie erwarten. */
export const DATA_ELEMENT_KINDS = { feed: 'feed', calendar: 'calendar', table: 'table' };
export const isDataElementType = (t) => Object.prototype.hasOwnProperty.call(DATA_ELEMENT_KINDS, t);
/** Datenquellen, die die Bausteine eines Layouts verwenden (nur freie Folien). */
export function dataSourceRefs(layout) {
    const l = layout;
    if (!l || typeof l !== 'object' || l.specVersion === 1 || !Array.isArray(l.elements))
        return [];
    const out = new Set();
    for (const e of l.elements) {
        if (e && typeof e.type === 'string' && isDataElementType(e.type) && typeof e.source === 'string' && e.source)
            out.add(e.source);
    }
    return [...out];
}
export const DataBindingSchema = z.object({
    type: z.literal('data'),
    source: z.string().min(1).max(64),
    column: z.string().max(64).optional(),
    map: z.object({ time: z.string().max(64).optional(), course: z.string().max(64).optional(), trainer: z.string().max(64).optional(), room: z.string().max(64).optional() }).optional(),
    limit: z.number().int().min(1).max(200).optional(),
    separator: z.string().max(20).optional(),
});
export const isDataBinding = (v) => !!v && typeof v === 'object' && v.type === 'data' && typeof v.source === 'string';
const clean = (s) => s.replace(/\|/g, '/').replace(/\s+/g, ' ').trim();
/**
 * Belegung → Text für ein Feld. target "rows" = Kursplan (eine Zeile je Kurs, "Zeit | Kurs | Trainer | Raum"),
 * "text" = Text/Laufschrift. null, wenn die Daten nicht passen.
 */
export function bindingToText(b, data, target) {
    const limit = Math.max(1, Math.min(200, b.limit ?? (target === 'rows' ? 50 : 20)));
    if (data.kind === 'table') {
        const idx = (key, fallback) => {
            if (key === '')
                return -1;
            const i = key ? data.columns.findIndex((c) => c.key === key) : -1;
            return i >= 0 ? i : key ? -1 : fallback < data.columns.length ? fallback : -1;
        };
        const rows = data.rows.slice(0, limit);
        if (target === 'rows') {
            const cols = [idx(b.map?.time, 0), idx(b.map?.course, 1), idx(b.map?.trainer, 2), idx(b.map?.room, 3)];
            return rows.map((r) => cols.map((i) => (i >= 0 ? clean(r[i] ?? '') : '')).join(' | ').replace(/( \| )+$/, '')).filter((l) => l.replace(/[|\s]/g, '')).join('\n');
        }
        const i = idx(b.column, 0);
        if (i < 0)
            return null;
        return rows.map((r) => (r[i] ?? '').trim()).filter(Boolean).join(b.separator ?? '\n');
    }
    if (data.kind === 'feed') {
        const items = data.items.slice(0, limit);
        if (target === 'rows')
            return items.map((it) => ` | ${clean(it.title)}`).join('\n');
        return items.map((it) => it.title.trim()).filter(Boolean).join(b.separator ?? '\n');
    }
    return null;
}
// ---------- Zeit (Zeitzone des Bildschirms) ----------
/** Kalendertag "YYYY-MM-DD" eines Zeitpunkts in einer Zeitzone. */
export function ymdIn(at, timeZone) {
    const d = new Date(at + tzOffsetMs(at, timeZone));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
/** Zeitpunkt von Mitternacht eines Kalendertags in einer Zeitzone (beachtet Sommerzeit). */
export function zonedMidnight(ymd, timeZone) {
    const [y, m, d] = ymd.split('-').map(Number);
    const guess = Date.UTC(y, m - 1, d);
    let t = guess - tzOffsetMs(guess, timeZone);
    // zweiter Schritt für Tage mit Zeitumstellung
    t = guess - tzOffsetMs(t, timeZone);
    return t;
}
/** Kalendertag + n Tage. */
export function addDays(ymd, n) {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
/** Beginn und Ende eines Termins als Zeitpunkte (ganztägig: Mitternacht in der Zeitzone). */
export function eventSpan(e, timeZone) {
    if (e.allDay) {
        const start = zonedMidnight(e.start.slice(0, 10), timeZone);
        const endDay = e.end && /^\d{4}-\d{2}-\d{2}/.test(e.end) ? e.end.slice(0, 10) : addDays(e.start.slice(0, 10), 1);
        return { start, end: Math.max(start + 1, zonedMidnight(endDay, timeZone)) };
    }
    const start = Date.parse(e.start);
    const end = Date.parse(e.end);
    return { start, end: Number.isFinite(end) && end > start ? end : start };
}
/** Termine, die einen Kalendertag (in der Zeitzone) berühren, sortiert: ganztägig zuerst, dann nach Beginn. */
export function eventsOnDay(events, ymd, timeZone) {
    const from = zonedMidnight(ymd, timeZone);
    const until = zonedMidnight(addDays(ymd, 1), timeZone);
    return events
        .map((e) => ({ e, s: eventSpan(e, timeZone) }))
        .filter(({ s }) => s.start < until && (s.end > from || (s.end === s.start && s.start >= from)))
        .sort((a, b) => Number(b.e.allDay) - Number(a.e.allDay) || a.s.start - b.s.start || a.e.title.localeCompare(b.e.title))
        .map(({ e }) => e);
}
const WEEKDAYS = [
    ['sonntag', 'so', 'sunday', 'sun'], ['montag', 'mo', 'monday', 'mon'], ['dienstag', 'di', 'tuesday', 'tue', 'tues'],
    ['mittwoch', 'mi', 'wednesday', 'wed'], ['donnerstag', 'do', 'thursday', 'thu', 'thur', 'thurs'], ['freitag', 'fr', 'friday', 'fri'],
    ['samstag', 'sa', 'saturday', 'sat', 'sonnabend'],
];
/**
 * Passt ein Zellwert zum Kalendertag? Erkennt "YYYY-MM-DD", "DD.MM.YYYY", "D.M.YY", "DD/MM/YYYY" sowie Wochentage
 * (deutsch/englisch, ausgeschrieben oder kurz) – z. B. für einen Kursplan je Wochentag oder Tageszeiten je Datum.
 */
export function cellMatchesDay(value, ymd) {
    const v = value.trim().toLowerCase().replace(/\.$/, '');
    if (!v)
        return false;
    const [y, m, d] = ymd.split('-').map(Number);
    let mm;
    if ((mm = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(v)))
        return Number(mm[1]) === y && Number(mm[2]) === m && Number(mm[3]) === d;
    if ((mm = /^(\d{1,2})[./](\d{1,2})[./](\d{2,4})/.exec(v))) {
        const yy = Number(mm[3]);
        return Number(mm[1]) === d && Number(mm[2]) === m && (yy < 100 ? 2000 + yy : yy) === y;
    }
    if ((mm = /^(\d{1,2})\.(\d{1,2})$/.exec(v)))
        return Number(mm[1]) === d && Number(mm[2]) === m;
    const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    return WEEKDAYS[wd].includes(v);
}
//# sourceMappingURL=data.js.map